import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import {toast} from 'react-toastify';
import {roomSelector} from '../../../../store/selectors/roomRunFile.selectors';

const WARNING_THRESHOLDS_MS = [10 * 60 * 1000, 5 * 60 * 1000];
const COUNTDOWN_VISIBLE_MS = 60 * 1000;
const TICK_MS = 1000;

function RoomEndCountdown(): JSX.Element | null {
    const {t} = useTranslation();
    const room = useSelector(roomSelector);
    const {endTime} = room;

    const [remainingMs, setRemainingMs] = useState<number | null>(null);
    const prevRemainingRef = useRef<number | null>(null);

    useEffect(() => {
        prevRemainingRef.current = null;

        if (!endTime) {
            setRemainingMs(null);
            return undefined;
        }

        const endMs = new Date(endTime).getTime();
        if (Number.isNaN(endMs)) {
            setRemainingMs(null);
            return undefined;
        }

        const tick = (): void => {
            const remaining = endMs - Date.now();
            const prev = prevRemainingRef.current;

            WARNING_THRESHOLDS_MS.forEach((threshold) => {
                if (prev !== null && prev > threshold && remaining <= threshold) {
                    toast.warning(t('roomSchedule.endWarning', {minutes: Math.round(threshold / 60000)}), {
                        toastId: `roomEndWarning-${threshold}`,
                    });
                }
            });

            prevRemainingRef.current = remaining;
            setRemainingMs(remaining);
        };

        tick();
        const interval = setInterval(tick, TICK_MS);
        return () => clearInterval(interval);
    }, [endTime, t]);

    if (remainingMs === null || remainingMs <= 0 || remainingMs > COUNTDOWN_VISIBLE_MS) {
        return null;
    }

    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const formatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                bottom: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                borderRadius: '999px',
                backgroundColor: 'rgba(17, 17, 17, 0.9)',
                color: '#fff',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
                fontVariantNumeric: 'tabular-nums',
                pointerEvents: 'none',
            }}
        >
            <Icon icon="solar:clock-circle-bold" style={{fontSize: '20px'}} />
            <span style={{fontSize: '18px', fontWeight: 700}}>{formatted}</span>
            <span style={{fontSize: '13px', opacity: 0.8}}>{t('roomSchedule.closingIn')}</span>
        </div>
    );
}

export default RoomEndCountdown;
